import qs from 'querystringify';
import assign from 'lodash/assign';
import merge from 'lodash/merge';
import { LocalStorage as Cache } from 'ttl-localstorage';
import {
  CLIENT_TYPE_HEADER,
  CLIENT_TYPE,
} from '@upstart/harmony/lib/constants';
import { cloneDeep } from 'lodash';

import {
  CLIENT_CODENAMES,
  X_UAR_HTTP_HEADERS,
} from '@upstart/harmony/lib/legos/constants/system';
import { MessageType } from '@upstart/harmony/lib/legos/websiteTypes';
import { getToken } from '../util/tokenUtils';
import { broadcastNewSessionTimeout } from '../util/sessionTimeout';
import getDealIdHeader from '../util/dealIdHeader';
import store from '../redux/store';
import { changeSystemStatus } from '../redux/global/actions';
import { forwardSessionTimeoutUpdateToCC } from '../util/commandCenter';
import { PageMessage } from '../components/GlobalUI';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const SYSTEM_STATUS_URL = process.env.REACT_APP_SYSTEM_STATUS_URL;

const DEFAULT_HEADERS = {
  [CLIENT_TYPE_HEADER]: CLIENT_TYPE.IN_STORE,
  [X_UAR_HTTP_HEADERS['X-UAR-Client-Codename']]: CLIENT_CODENAMES.CHAIRMAN,
};
const CONTENT_TYPE_HEADER = {
  'Content-Type': 'application/json',
};

const SESSION_DURATION_HEADER = X_UAR_HTTP_HEADERS['X-UAR-Session-Duration'];

Cache.synchronous = true;
Cache.timeoutInSeconds = 600;

const FORBIDDEN = 'Forbidden';

export default class BackendService {
  static async get(url, options = {}) {
    const fetchOptions = assign(options, { method: 'GET' });
    const cacheKey = BackendService.getFullUrl(url, fetchOptions, false);
    const cachingEnabled = fetchOptions.cache;

    // Check if value is already fetched
    if (cachingEnabled) {
      const cachedResponse = Cache.get(cacheKey);
      if (cachedResponse) {
        return cachedResponse;
      }
    }
    const response = await BackendService.callFetch(url, fetchOptions);

    if (cachingEnabled) {
      Cache.put(cacheKey, response);
    }
    return response;
  }

  static post(url, options, noRequestHeaders) {
    const fetchOptions = merge(
      {
        method: 'POST',
        headers: options?.formData ? {} : cloneDeep(CONTENT_TYPE_HEADER),
      },
      options,
    );
    if (noRequestHeaders) {
      delete fetchOptions.headers;
    }
    return BackendService.callFetch(url, fetchOptions);
  }

  // Create as if we were creating online
  static postWithoutToken(url, dealerId, options) {
    const fetchOptions = merge(
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${dealerId}`,
        },
      },
      options,
    );
    return BackendService.callFetch(url, fetchOptions, false);
  }

  static put(url, options, noRequestHeaders) {
    const fetchOptions = merge(
      {
        method: 'PUT',
        headers: cloneDeep(CONTENT_TYPE_HEADER),
      },
      options,
    );
    if (noRequestHeaders) {
      delete fetchOptions.headers;
    }
    return BackendService.callFetch(url, fetchOptions);
  }

  static patch(url, options, noRequestHeaders) {
    const fetchOptions = merge(
      {
        method: 'PATCH',
        headers: cloneDeep(CONTENT_TYPE_HEADER),
      },
      options,
    );
    if (noRequestHeaders) {
      delete fetchOptions.headers;
    }
    return BackendService.callFetch(url, fetchOptions);
  }

  static delete(url, options) {
    const fetchOptions = assign(options, {
      method: 'DELETE',
      headers: cloneDeep(CONTENT_TYPE_HEADER),
    });
    return BackendService.callFetch(url, fetchOptions);
  }

  static getFullUrl = (url, options, useToken = true) => {
    let fullUrl = BACKEND_URL;
    if (url) {
      fullUrl += url;
    }

    const params = options.params || {};
    if (useToken) {
      const token = getToken();
      if (token) {
        params.access_token = token;
      }
    }

    fullUrl += qs.stringify(params, true);
    return fullUrl;
  };
  static getFetchOptions = (options, useToken = true) => {
    const dealIdHeader = getDealIdHeader();
    const fetchOptions = { headers: DEFAULT_HEADERS };
    if (useToken) {
      const token = getToken();
      if (token) {
        fetchOptions.headers = {
          ...dealIdHeader,
          ...fetchOptions.headers,
          'x-uar-access-token': `Bearer ${token}`,
        };
      }
    }
    if (options.method) {
      fetchOptions.method = options.method;
    }
    if (options.headers) {
      fetchOptions.headers = { ...fetchOptions.headers, ...options.headers };
    }
    if (options.body) {
      fetchOptions.body = JSON.stringify(options.body, (_, value) =>
        typeof value === 'undefined' ? null : value,
      );
    }
    if (options.formData) {
      fetchOptions.body = options.formData;
    }
    fetchOptions.credentials = 'include';
    return fetchOptions;
  };

  // use_token identifies the request with a user
  // if true, this creates deals as if we were creating them with `papyrus`
  // if false, this creates deals as if we were creating them with `online`
  static async callFetch(url, options, useToken = true) {
    try {
      const fullUrl = BackendService.getFullUrl(url, options, false);
      const fetchOptions = BackendService.getFetchOptions(options, useToken);
      const response = await fetch(fullUrl, fetchOptions);

      const hasSessionDurationHeader = !!response.headers.get(
        SESSION_DURATION_HEADER,
      );
      const parsedTimeoutHeader = parseInt(
        response.headers.get(SESSION_DURATION_HEADER),
        10,
      );
      if (hasSessionDurationHeader && Number.isFinite(parsedTimeoutHeader)) {
        broadcastNewSessionTimeout(parsedTimeoutHeader);

        // if chairman is being run in an iframe inside command center, be sure to notify
        // command center of session duration changes
        forwardSessionTimeoutUpdateToCC(parsedTimeoutHeader);
      }

      if (
        !!options.params &&
        !!options.params.type &&
        options.params.type === 'blob'
      ) {
        // If type is present and equals 'blob'
        // return resolved promise with blob
        const blob = await response.blob();
        return response.ok ? Promise.resolve(blob) : Promise.reject(blob);
      }

      if (options.responseType === 'text' && response.ok) {
        return await response.text();
      }

      // Default return method resolves promise with JSON
      if (response.ok) {
        const json = response.json();
        return Promise.resolve(json);
      }
      const error = await response.text();
      let message = '';
      try {
        const errorObject = JSON.parse(error);
        message =
          errorObject?.message ?? errorObject?.error ?? response.statusText;
        if (response.statusText === FORBIDDEN) {
          PageMessage({
            message,
            type: MessageType.ERROR,
          });
        }
      } catch (e) {
        /* NOOP */
      }

      try {
        const systemStatusResponse = await fetch(SYSTEM_STATUS_URL);
        if (systemStatusResponse.ok) {
          const systemStatus = await systemStatusResponse.json();
          store.dispatch(
            changeSystemStatus({
              isSystemDown:
                systemStatus.isPlannedMaintenance ||
                systemStatus.isUnplannedMaintenance ||
                systemStatus.isAcknowledgedDowntime,
              restoreDate: systemStatus.estimateTimeForNormalization,
            }),
          );
        }
      } catch (e) {
        const errorData = { error: 'cannot fetch system status' };
        return Promise.reject(errorData);
      }

      return Promise.reject({ status: response.status, error, message }); // eslint-disable-line
    } catch (error) {
      const errorData = { error: 'An unknown error occurred' };
      return Promise.reject(errorData);
    }
  }
}
